import type { KidsPolicyItemInput, OverSiteControllerPeopleNumInput } from "@/lib/gql/__generated__/graphql";
import { CurrencyType, type RoomInput } from "@/lib/gql/__generated__/graphql";

import { convertRequiredPrepMillisecondBeforeDateAndHourIntoRequiredPrepMillisecond } from "@/components/features/_shared/validation-schema/converter";

import type { ProductRoomFormModel } from "../../../type";

type Props = {
	viewModel: ProductRoomFormModel["ViewModel"];
};

export const viewModelToRoomInputConverter = ({ viewModel }: Props): RoomInput => {
	// latとlonが両方とも存在しているかどうか
	const { geoLocation } = viewModel;
	const geoLocationExists =
		!!geoLocation &&
		geoLocation.lat !== null &&
		geoLocation.lat !== undefined &&
		geoLocation.lon !== null &&
		geoLocation.lon !== undefined;

	const planInput: RoomInput = {
		hotelID: viewModel.hotelID,
		roomID: viewModel.roomID,

		availablePlanIDs: viewModel.availablePlanIDs.value.map((planID) => planID.value),
		name: viewModel.name.value,

		address: {
			city: viewModel.address?.city?.value,
			nationality: viewModel.address?.nationality?.value,
			other: viewModel.address?.other?.value,
			postalCode: viewModel.address?.postalCode?.value,
			pref: viewModel.address?.pref?.value,
			state: viewModel.address?.state?.value,
			town: viewModel.address?.town?.value,
		},
		amenities: viewModel.amenities.value.map((amenity) => amenity.value),
		beds: viewModel.beds.value.map((bed) => {
			return {
				bedNum: bed.bedNum.value,
				bedType: bed.bedType.value,
			} satisfies RoomInput["beds"][0];
		}),
		capacityAdultMaximum: viewModel.capacityAdultMaximum.value,
		capacityKidsMaximum: viewModel.capacityKidsMaximum.value,
		capacityPeopleMaximum: viewModel.capacityPeopleMaximum.value,
		catchCopy: viewModel.catchCopy.value,
		description: viewModel.description.value,
		geoLocation: geoLocationExists
			? ({
					lat: geoLocation.lat!.value!,
					lon: geoLocation.lon!.value!,
				} satisfies RoomInput["geoLocation"])
			: undefined,
		inSale: viewModel.inSale.value,
		isDormitoryType: viewModel.isDormitoryType.value,
		isShowStockPreview: viewModel.isShowStockPreview?.value ?? false,
		isExtendSiteControllerPeopleNum: viewModel.isExtendSiteControllerPeopleNum.value,
		isKidsUnavailable: viewModel.isKidsUnavailable.value,
		isNotDiscountTarget: viewModel.isNotDiscountTarget?.value ?? false,
		keyVisuals: viewModel.keyVisuals.value.length
			? viewModel.keyVisuals.value.map((keyVisual) => {
					return {
						bucket: keyVisual.bucket.value,
						fileName: keyVisual.fileName?.value,
						key: keyVisual.key.value,
						mimeType: keyVisual.mimeType?.value,
						region: keyVisual.region.value,
					} satisfies RoomInput["mainKeyVisual"];
				})
			: undefined,
		kidsPolicy: viewModel.kidsPolicy?.value?.length
			? viewModel.kidsPolicy.value.map((item) => {
					return {
						kidsDiscountType: item.kidsDiscountType.value,
						kidsMaxAge: item.kidsMaxAge.value,
						ratio: item.ratio?.value,
						static: {
							amount: item.static?.amount.value ?? 0,
							currencyType: item.static?.currencyType.value ?? CurrencyType.Jpy,
						},
					} satisfies KidsPolicyItemInput;
				})
			: undefined,

		mainKeyVisual: {
			bucket: viewModel.mainKeyVisual.bucket.value,
			fileName: viewModel.mainKeyVisual.fileName?.value,
			key: viewModel.mainKeyVisual.key.value,
			mimeType: viewModel.mainKeyVisual.mimeType?.value,
			region: viewModel.mainKeyVisual.region.value,
		} satisfies RoomInput["mainKeyVisual"],
		maxRoomNum: viewModel.maxRoomNum.value,
		overSiteControllerPeopleNum: viewModel.overSiteControllerPeopleNum?.value?.map((item) => {
			return {
				availableWeekDays: item.availableWeekDays.value.map((day) => day.value),
				prices: item.prices.value.map((price) => {
					return {
						price: {
							amount: price.price.amount.value,
							currencyType: price.price.currencyType.value,
						},
						upperNum: price.upperNum.value,
					};
				}),
			} satisfies OverSiteControllerPeopleNumInput;
		}),
		requiredPrepMillisecond: convertRequiredPrepMillisecondBeforeDateAndHourIntoRequiredPrepMillisecond({
			requiredPrepMillisecondBeforeDate: viewModel.requiredPrepMillisecondBeforeDate.value,
			requiredPrepMillisecondBeforeHour: viewModel.requiredPrepMillisecondBeforeHour.value,
		}),
		roomSize: viewModel.roomSize?.value ?? undefined,
		saleTypeForPlan: viewModel.saleTypeForPlan.value,
	} satisfies RoomInput;

	return planInput;
};
